import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { keys } from '../keys.ts'
import { createQuery } from '../query.ts'

/**
 * 레시피 편집기(V2)가 읽는 **백엔드 정적 자원** 둘 — `/api` 밖(무인증 정적 파일)이라 오케스트레이터 클라이언트를 타지 않는다.
 *
 * | 자원 | 경로 | 쓰임 |
 * | --- | --- | --- |
 * | 편집층 스키마 | `/resources/schema/recipe_v2_data.schema.json` | 염색 스텝 폼 칸(`#/definitions/liquidStep`) |
 * | 데크 정의 | `/resources/admin/decks/<deckId>.json` | 데크 그림(칸 · 폐기 자리 위치) |
 *
 * 🔴 프록시 두 줄이 있어야 닿는다(`packages/build-config` · 배포 nginx). 못 받으면 **에러로 돌려주고** 편집기는 꺾여 선다
 *    (데크 그림 없이 표 · 스텝 폼은 그 스텝 칸만) — 막다른 화면을 만들지 않는다.
 * ⚠ 파일이 바뀔 일이 드물어 한 번 받으면 다시 묻지 않는다(무효화 대상 아님 · 폴링 아님).
 */
export const RECIPE_DATA_SCHEMA_PATH = '/resources/schema/recipe_v2_data.schema.json'
export const DECK_DEFINITION_DIR = '/resources/admin/decks/'

export type StaticJson = Record<string, unknown>

async function getStaticJson(path: string): Promise<StaticJson> {
  const response = await fetch(path, {
    signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS.command),
    headers: { Accept: 'application/json' },
  })
  // SPA fallback 이 index.html 을 200 으로 줄 수 있다 — JSON 이 아니면 실패로 본다(가짜 200)
  const type = response.headers.get('content-type') ?? ''
  if (!response.ok || !type.includes('json')) {
    throw Object.assign(new Error(`static_resource_unavailable:${String(response.status)}`), {
      status: response.status,
    })
  }
  return (await response.json()) as StaticJson
}

export const useRecipeDataSchema = createQuery<{ skip?: boolean } | void, StaticJson>({
  key: () => keys.templates.detail('resource:recipe-data-schema'),
  fetch: () => getStaticJson(RECIPE_DATA_SCHEMA_PATH),
})

export const useDeckDefinition = createQuery<
  { deckId: string | undefined; skip?: boolean },
  StaticJson
>({
  key: ({ deckId }) => keys.templates.detail(`resource:deck:${deckId ?? ''}`),
  fetch: ({ deckId }) =>
    getStaticJson(`${DECK_DEFINITION_DIR}${encodeURIComponent(String(deckId))}.json`),
  // 🔴 데크 이름은 문서가 정한다(`robot.deckId`) — 모르면 부르지 않는다(기본 데크를 지어 그리지 않는다)
  enabled: ({ deckId }) => deckId !== undefined && /^[\w.-]+$/.test(deckId),
})
